<script setup lang="ts">
/**
 * PMS top header bar — gray-100 background, FZ:Partner wordmark,
 * top-level horizontal nav, notification + help icon buttons,
 * avatar bubble.
 *
 * Pure presentation. Active nav item, click handlers, and the
 * avatar initials are passed as props/emits. Phase-2 view code
 * resolves them from auth + route.
 */
import CdsIcon from '../atoms/CdsIcon.vue'

export interface PmsHeaderNavItem {
  id: string
  label: string
  href?: string
}

withDefaults(
  defineProps<{
    navItems: PmsHeaderNavItem[]
    activeId?: string
    avatarInitials?: string
    avatarTitle?: string
    /** Hide the hamburger when the surface has no side-nav. */
    showHamburger?: boolean
    /** Reflects whether the side-nav is currently open. Affects icon. */
    navOpen?: boolean
  }>(),
  {
    showHamburger: true,
    navOpen: true,
    avatarInitials: '',
    avatarTitle: '',
    activeId: undefined,
  },
)

const emit = defineEmits<{
  toggleNav: []
  selectNav: [id: string]
  notificationsClick: []
  helpClick: []
  avatarClick: []
}>()

function pickNav(item: PmsHeaderNavItem) {
  emit('selectNav', item.id)
}
</script>

<template>
  <header class="fz-pms-header">
    <button
      v-if="showHamburger"
      type="button"
      class="fz-pms-header__icon-btn fz-pms-header__hamburger"
      :aria-label="navOpen ? 'Chiudi menu' : 'Apri menu'"
      @click="emit('toggleNav')"
    >
      <CdsIcon :name="navOpen ? 'close' : 'menu'" :size="20" />
    </button>
    <a class="fz-pms-header__brand" href="#">
      FZ<span class="fz-pms-header__brand-sep">:</span><strong>Partner</strong>
    </a>
    <span class="fz-pms-header__divider" aria-hidden="true" />
    <nav class="fz-pms-header__nav" aria-label="Sezioni globali">
      <button
        v-for="item in navItems"
        :key="item.id"
        type="button"
        :class="['fz-pms-header__nav-item', { 'is-active': activeId === item.id }]"
        @click="pickNav(item)"
      >
        {{ item.label }}
      </button>
    </nav>
    <span class="fz-pms-header__spacer" />
    <button
      type="button"
      class="fz-pms-header__icon-btn"
      aria-label="Notifiche"
      @click="emit('notificationsClick')"
    >
      <CdsIcon name="bell" :size="20" />
    </button>
    <button
      type="button"
      class="fz-pms-header__icon-btn"
      aria-label="Aiuto"
      @click="emit('helpClick')"
    >
      <CdsIcon name="help" :size="20" />
    </button>
    <button
      type="button"
      class="fz-pms-header__avatar"
      :title="avatarTitle"
      :aria-label="avatarTitle || 'Profilo'"
      @click="emit('avatarClick')"
    >
      {{ avatarInitials }}
    </button>
  </header>
</template>

<style scoped>
.fz-pms-header {
  display: flex;
  align-items: center;
  background: var(--gray-100);
  color: var(--text-inverse);
  height: 48px;
  border-bottom: 1px solid var(--gray-90);
}
.fz-pms-header__hamburger,
.fz-pms-header__icon-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--icon-inverse);
  cursor: pointer;
  flex: 0 0 48px;
  font: inherit;
}
.fz-pms-header__icon-btn:hover {
  background: var(--gray-90);
}
.fz-pms-header__icon-btn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.fz-pms-header__brand {
  font-size: var(--fs-14);
  color: var(--text-inverse);
  text-decoration: none;
  padding: 0 var(--spacing-05);
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  font-weight: var(--fw-regular);
  letter-spacing: -0.2px;
}
.fz-pms-header__brand strong {
  font-weight: var(--fw-semibold);
}
.fz-pms-header__brand-sep {
  color: var(--blue-40);
}
.fz-pms-header__divider {
  width: 1px;
  height: 24px;
  background: var(--gray-80);
  margin: 0 var(--spacing-04);
}
.fz-pms-header__nav {
  display: flex;
  height: 100%;
}
.fz-pms-header__nav-item {
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-05);
  color: var(--gray-30);
  text-decoration: none;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  font-size: var(--fs-14);
  font-family: inherit;
  cursor: pointer;
}
.fz-pms-header__nav-item:hover {
  background: var(--gray-90);
  color: var(--text-inverse);
}
.fz-pms-header__nav-item.is-active {
  color: var(--text-inverse);
  border-bottom-color: var(--blue-60);
}
.fz-pms-header__nav-item:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.fz-pms-header__spacer {
  flex: 1;
}
.fz-pms-header__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--blue-60);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-12);
  font-weight: var(--fw-semibold);
  margin: 0 var(--spacing-04) 0 var(--spacing-03);
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.fz-pms-header__avatar:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}
</style>
